import React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, Gem } from "lucide-react";
import { buildWhatsAppLink, generalEnquireMessage, INSTAGRAM_LINK } from "@/lib/whatsapp";
import { scrollToId } from "@/lib/scroll";
import { FloatingJewelry } from "@/components/FloatingJewelry";

export const Hero = () => {
  return (
    <section id="home" className="relative pt-28 pb-20 sm:pt-36 sm:pb-28 overflow-hidden bg-gradient-dark">
      <div className="absolute inset-0 bg-dot-grid opacity-30" aria-hidden="true" />
      <FloatingJewelry />
      <div
        className="absolute -top-40 left-1/2 -translate-x-1/2 w-[640px] h-[640px] rounded-full pointer-events-none"
        style={{ background: "var(--gradient-radial-glow)" }}
        aria-hidden="true"
      />
      <div className="noise-overlay" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-10 items-center">
        <div className="text-center lg:text-left">
          <span className="section-eyebrow justify-center lg:justify-start">
            <Sparkles className="h-3.5 w-3.5" /> Gold &middot; Silver &middot; Fine Jewellery
          </span>

          <h1 className="mt-5 font-display text-5xl sm:text-6xl lg:text-[4.5rem] font-semibold leading-[1.05] text-foreground">
            Vini Gems
            <br />
            <span className="text-gradient-gold">And Jewellers</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 leading-relaxed">
            Browse our gold, silver and specially curated jewellery pieces. Tap on anything you love and
            connect with us instantly on WhatsApp for pricing, purity details &amp; custom orders.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Button size="xl" variant="premium" onClick={() => scrollToId("gold")} data-testid="hero-explore-btn">
              Explore Gold Collection <ArrowRight className="h-4 w-4" />
            </Button>
            <Button asChild size="xl" variant="whatsapp" data-testid="hero-whatsapp-btn">
              <a href={buildWhatsAppLink(generalEnquireMessage())} target="_blank" rel="noopener noreferrer">
                <i className="fa-brands fa-whatsapp text-lg" /> Enquire on WhatsApp
              </a>
            </Button>
          </div>

          <div className="mt-11 flex items-center gap-8 sm:gap-10 justify-center lg:justify-start">
            {["18K", "22K", "24K"].map((p) => (
              <div key={p} className="flex flex-col items-center lg:items-start">
                <span className="font-heading text-2xl sm:text-3xl text-primary">{p}</span>
                <span className="text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground mt-0.5">
                  Gold Purity
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mx-auto max-w-sm sm:max-w-md w-full">
          <div
            className="absolute -inset-6 rounded-[2rem] opacity-25 blur-2xl"
            style={{ background: "var(--gradient-gold)" }}
            aria-hidden="true"
          />
          <div className="relative rounded-[2rem] border border-primary/25 bg-card p-3 shadow-elegant animate-fade-up">
            <img
              src="/images/jewellery/hero-pendant-01.png"
              alt="Gold Lakshmi pendant with peacock jhumkis - Vini Gems And Jewellers"
              className="w-full h-[360px] sm:h-[440px] object-cover rounded-[1.6rem]"
            />

            <div className="absolute -bottom-6 -left-4 sm:-left-6 bg-card border border-border rounded-2xl px-4 sm:px-5 py-3 shadow-card-lux flex items-center gap-3 animate-float">
              <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center shrink-0">
                <Gem className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">Handpicked Designs</p>
                <p className="text-xs text-muted-foreground">Gold &amp; Silver Collection</p>
              </div>
            </div>

            <a
              href={INSTAGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute -top-5 -right-3 sm:-right-5 bg-card border border-border rounded-full pl-3 pr-4 py-2 shadow-card-lux flex items-center gap-2 hover:border-primary transition-colors duration-300"
              data-testid="hero-instagram-badge"
            >
              <i className="fa-brands fa-instagram text-primary" />
              <span className="text-xs font-medium text-foreground">Follow Us</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
